import React, { useEffect, useRef, useState } from "react";
import { useAppSelector, useAppDispatch } from "hooks/redux";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import axios, { CancelToken, CancelTokenSource } from "axios";

import {
  GetTeeSheetNotificationsByDate,
  GetTeeTimeNew,
  GetTeeTimeBookingCategory,
  GetAllTeeTimeCartsAvailable,
  ITeeSheet,
  GetTeeSheets,
  ITeeTimeCartsAvailable,
} from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import { StatusCode } from "api/protocols";

import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";
import { formatDate } from "helpers/Helpers";

import { selectDate } from "redux/actions/teesheet";
import { showError } from "redux/actions/ui";
import { loadFacilityWeather } from "redux/actions/facility";

import NewStatisticsBar from "./elements/NewStatisticsBar";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import DateBar from "elements/teesheet/DateBar";
import NewTeeSheetTabs from "./NewTeeSheetTabs";
import Callout from "components/callout/Callout";
import Spin from "components/spin/spin";
import { Select } from "components/select/index";
import teeSheetToast from "components/teeSheetToast/TeeSheetToast";
import Input from "components/form/input";

import { INotification } from "api/rpc/2024-04/customer/teesheet";
import { ICourse, IDivision } from "redux/reducers/models/facility";
import { ITeeTimeNew, ITeeTimeBookingCategory, ITeeTimeSlotNew } from "redux/reducers/models/teetime";

import "../styles.scss";
import { cloneDeep, uniqBy } from "lodash";
import {
  loadingTeetimeTabActionsAreEqual,
  TLoadingTeetimeTabsActionStatus,
  TNewTeeSheetLoadingTeetimeTabAction,
} from "./loadingTeetimeTabActions";

interface INewTeeSheetState {
  notifications: INotification[];
  currentCourseId: number;
  currentDivisions: IDivision[];
  currentDivisionId: number;
  teeTimeSearch: string;
}

export interface ITeeTimeTabNew extends ITeeTimeNew {
  power_cart_quantity?: number;
  carts_available?: number;
  power_cart_numbers?: string;
}

export default function NewTeeSheet() {
  const windowSize = useWindowSize();
  const { teeSheetStore, facilityStore } = useAppSelector(store => store);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const tabletDateBar = useRef<HTMLDivElement>(null);

  const [teeSheet, setTeeSheet] = useState<ITeeSheet>(undefined);
  const [teetimeTabs, setTeetimeTabs] = useState<ITeeTimeTabNew[]>(undefined);
  const [teeTimeBookingCategories, setTeeTimeBookingCategories] = useState<ITeeTimeBookingCategory[][]>(undefined);

  const [loadingTeetimeTabActions, setLoadingTeetimeTabActions] = useState<TNewTeeSheetLoadingTeetimeTabAction[]>([]);

  const [state, setState] = useState<INewTeeSheetState>({
    notifications: null,
    currentCourseId: null,
    currentDivisions: null,
    currentDivisionId: null,
    teeTimeSearch: "",
  });

  const teeSheetExitCancelSource = useRef<CancelTokenSource>(axios.CancelToken.source());

  useEffect(() => {
    teeSheetExitCancelSource.current = axios.CancelToken.source();

    return () => teeSheetExitCancelSource.current.cancel();
  }, []);

  useEffect(() => {
    if (facilityStore.facility) {
      void dispatch(loadFacilityWeather(facilityStore.facility.id, true));

      const currentDivision = parseInt(
        localStorage.getItem(`${facilityStore.facility.short_name}_tee_sheet_current_division`),
      );
      const currentCourseId = parseInt(
        localStorage.getItem(`${facilityStore.facility.short_name}_tee_sheet_current_course_id`),
      );
      const currentDivisions = facilityStore.facility.courses.find(
        course => course.id === currentCourseId,
      )?.divisions;

      const localStorageDataIsValid = currentDivision && currentCourseId && currentDivisions;

      setState(prevState => ({
        ...prevState,
        currentDivisionId: localStorageDataIsValid
          ? currentDivision
          : facilityStore.facility.courses[0]?.divisions[0]?.id,
        currentDivisions: localStorageDataIsValid ? currentDivisions : facilityStore.facility.courses[0].divisions,
        currentCourseId: localStorageDataIsValid ? currentCourseId : facilityStore.facility.courses[0].id,
      }));
    }
  }, [facilityStore.facility]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTeeSheetNotifications(teeSheetStore.selectedDate as Date, source.token);
    return () => source.cancel();
  }, [teeSheetStore.selectedDate]);

  // Update teetimes on course, division, or date change
  useEffect(() => {
    const source = axios.CancelToken.source();

    if (
      state.currentCourseId &&
      state.currentDivisionId &&
      teeSheetStore.selectedDate &&
      facilityStore.teeSheetSettings?.validate_power_cart_count != null
    ) {
      void loadTeeTimeTabs(state.currentDivisionId, state.currentDivisions, source.token);
      void loadTeeSheet(source.token);
      void loadTeeTimeBookingCategories(source.token);
    }

    return () => source.cancel();
  }, [
    state.currentCourseId,
    state.currentDivisionId,
    teeSheetStore.selectedDate,
    facilityStore.teeSheetSettings?.validate_power_cart_count,
  ]);

  // Save course and divison ids in local storage when they are updated
  useEffect(() => {
    if (state.currentCourseId && state.currentDivisionId && facilityStore.facility) {
      localStorage.setItem(
        `${facilityStore.facility.short_name}_tee_sheet_current_division`,
        String(state.currentDivisionId),
      );
      localStorage.setItem(
        `${facilityStore.facility.short_name}_tee_sheet_current_course_id`,
        String(state.currentCourseId),
      );
    }
  }, [state.currentCourseId, state.currentDivisionId]);

  function handleCourseChange(courseId: number) {
    let currentCourse: ICourse = undefined;
    if (facilityStore?.facility?.courses?.length > 0) {
      currentCourse = facilityStore.facility.courses.find(course => course.id === courseId);
    }

    if (currentCourse === undefined) {
      return;
    }

    setState(prevState => ({
      ...prevState,
      currentCourseId: currentCourse.id,
      currentDivisions: currentCourse.divisions,
      currentDivisionId: currentCourse.divisions[0]?.id,
    }));
  }

  function getPowerCartNumbers(slots: ITeeTimeSlotNew[]): string {
    const powerCartNumbers = slots.reduce((accumulator: number[], currentSlot) => {
      if (currentSlot.inventory_power_cart_number != null) {
        return [...accumulator, currentSlot.inventory_power_cart_number];
      } else if (currentSlot.power_cart_number != null) {
        return [...accumulator, currentSlot.power_cart_number];
      } else {
        return accumulator;
      }
    }, []);

    return powerCartNumbers.join("/");
  }

  function calculatePowerCartQuantity(teetime: ITeeTimeTabNew) {
    if (teetime.slots.some(slot => slot.turn === 1)) {
      return 0;
    }

    const uniqueBookedSlots = uniqBy(teetime.slots, slot => slot.tee_time_booking_id);
    const powerCartQuantity = uniqueBookedSlots.reduce(
      (accumulator, currentSlot) => accumulator + (currentSlot.power_cart_quantity ?? 0),
      0,
    );

    return powerCartQuantity;
  }

  async function loadTeeSheet(token?: CancelToken) {
    const teeSheetsRes = await GetTeeSheets(
      {
        date: formatDate(teeSheetStore.selectedDate as Date),
        extended_carts_available: true,
      },
      false,
      token,
    );

    if (token?.reason) {
      return;
    }

    if (teeSheetsRes.status !== StatusCode.OK) {
      dispatch(showError("Error loading tee sheet information")); // TODO: Translation
      return;
    }

    setTeeSheet(teeSheetsRes.data?.[0]);
  }

  function populateTeetimeTabWithCartsAvailable(
    teetimeTab: ITeeTimeTabNew,
    cartsAvailableList: ITeeTimeCartsAvailable[],
  ) {
    const cartsAvailable = cartsAvailableList.find(
      cartsAvailable => cartsAvailable.id === teetimeTab.slots[0].tee_time_id,
    );

    teetimeTab.carts_available = cartsAvailable?.carts_available ?? 0;
    teetimeTab.power_cart_quantity = calculatePowerCartQuantity(teetimeTab);
    teetimeTab.power_cart_numbers = getPowerCartNumbers(teetimeTab.slots);
  }

  function populateTeetimeTabsWithCartsAvailable(
    teetimeTabsToChange: ITeeTimeTabNew[],
    cartsAvailableList: ITeeTimeCartsAvailable[],
  ) {
    teetimeTabsToChange.forEach(teetimeTab => populateTeetimeTabWithCartsAvailable(teetimeTab, cartsAvailableList));
  }

  async function loadTeeTimeTabs(currentDivisionId: number, currentDivisions: IDivision[], token?: CancelToken) {
    setTeetimeTabs(undefined);

    let division_id: number = undefined;
    let division_ids: number[] = undefined;

    if (currentDivisionId != -1) {
      division_id = currentDivisionId;
    } else {
      division_ids = currentDivisions.map(division => division.id);
    }

    const formattedDate = formatDate(teeSheetStore.selectedDate as Date);

    const teetimesRes = await GetTeeTimeNew(
      {
        date: formattedDate,
        course_id: state.currentCourseId,
        division_id,
        division_ids,
      },
      token ? false : true,
      token,
    );

    if (token?.reason) {
      return;
    }

    if (teetimesRes.status !== StatusCode.OK) {
      dispatch(showError("Error loading tee times")); // TODO: Translation
      return;
    }

    const updatedTeetimeTabs: ITeeTimeTabNew[] = teetimesRes.data;

    if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
      const cartsAvailableRes = await GetAllTeeTimeCartsAvailable(
        { date: formattedDate, course_id: state.currentCourseId },
        token ? false : true,
        token,
      );

      if (token?.reason) {
        return;
      }

      if (cartsAvailableRes.status !== StatusCode.OK) {
        dispatch(showError("Unable to retrieve cart availability")); // TODO: Translation
        return;
      }

      populateTeetimeTabsWithCartsAvailable(updatedTeetimeTabs, cartsAvailableRes.data);
    }

    setTeetimeTabs(updatedTeetimeTabs);
  }

  async function loadActionTeeTimeTabs(
    loadingTeetimeTabAction: TNewTeeSheetLoadingTeetimeTabAction,
    useLoader: boolean,
  ): Promise<TLoadingTeetimeTabsActionStatus> {
    if (
      !state.currentCourseId ||
      !state.currentDivisionId ||
      facilityStore.teeSheetSettings?.validate_power_cart_count == null
    ) {
      dispatch(showError("Error loading tee times")); // TODO: Translation
      return "failure";
    }

    try {
      if (useLoader) {
        setLoadingTeetimeTabActions(prev => {
          return [...prev, loadingTeetimeTabAction];
        });
      }

      let division_id: number = undefined;
      let division_ids: number[] = undefined;

      if (state.currentDivisionId != -1) {
        division_id = state.currentDivisionId;
      } else {
        division_ids = state.currentDivisions.map(division => division.id);
      }

      const formattedDate = formatDate(teeSheetStore.selectedDate as Date);

      const teetimesRes = await GetTeeTimeNew(
        {
          ids:
            loadingTeetimeTabAction.type === "move-bookings" || loadingTeetimeTabAction.type === "add-bookings"
              ? loadingTeetimeTabAction.ids
              : [loadingTeetimeTabAction.id],
          date: formattedDate,
          course_id: state.currentCourseId,
          division_id,
          division_ids,
        },
        false,
        teeSheetExitCancelSource.current.token,
      );

      if (teeSheetExitCancelSource.current.token?.reason) {
        return "failure";
      }

      if (teetimesRes.status !== StatusCode.OK) {
        dispatch(showError("Error loading tee times")); // TODO: Translation
        return "failure";
      }

      let cartsAvailableList: ITeeTimeCartsAvailable[] = [];

      if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
        const cartsAvailableRes = await GetAllTeeTimeCartsAvailable(
          { date: formattedDate, course_id: state.currentCourseId },
          false,
          teeSheetExitCancelSource.current.token,
        );

        if (teeSheetExitCancelSource.current.token?.reason) {
          return "failure";
        }

        if (cartsAvailableRes.status !== StatusCode.OK) {
          dispatch(showError("Unable to retrieve cart availability")); // TODO: Translation
          return "failure";
        }

        cartsAvailableList = cartsAvailableRes.data;
      }

      setTeetimeTabs(prevTeetimeTabs => {
        if (prevTeetimeTabs == null) {
          return prevTeetimeTabs;
        } else {
          return prevTeetimeTabs.map(prevTeetimeTab => {
            const specificTeetime = teetimesRes.data.find(
              teetime => teetime.slots[0].tee_time_id === prevTeetimeTab.slots[0].tee_time_id,
            );
            const updatedTeetimeTab: ITeeTimeTabNew = specificTeetime ? specificTeetime : cloneDeep(prevTeetimeTab);

            if (facilityStore.teeSheetSettings?.validate_power_cart_count) {
              populateTeetimeTabWithCartsAvailable(updatedTeetimeTab, cartsAvailableList);
            }

            return updatedTeetimeTab;
          });
        }
      });

      return "success";
    } finally {
      if (useLoader) {
        setLoadingTeetimeTabActions(prev =>
          prev.filter(
            prevLoadingTeetimeTabAction =>
              !loadingTeetimeTabActionsAreEqual(prevLoadingTeetimeTabAction, loadingTeetimeTabAction),
          ),
        );
      }
    }
  }

  async function loadTeeTimeBookingCategories(token?: CancelToken) {
    const formattedDate = formatDate(teeSheetStore.selectedDate as Date);

    const categoryRes = await GetTeeTimeBookingCategory({ date: formattedDate }, false, token);

    if (token?.reason) {
      return;
    }

    if (categoryRes.status !== StatusCode.OK) {
      return;
    }

    setTeeTimeBookingCategories(categoryRes.data);
  }

  async function loadTeeSheetNotifications(date: Date, token?: CancelToken) {
    teeSheetToast.show([]);

    const formattedDate: string = formatDate(date);
    const notificationRes = await GetTeeSheetNotificationsByDate({ date: formattedDate }, false, token);

    if (token && token.reason) {
      return;
    }
    if (notificationRes.status !== StatusCode.OK) {
      dispatch(showError(t("elements.tee_sheet.new_tee_sheet.001")));
      return;
    }

    if (notificationRes.status === StatusCode.OK && notificationRes.data?.length !== 0) {
      const toastList: Array<{
        subject: string;
        content: string;
        duration?: number;
        onClose?: () => void;
      }> = notificationRes.data?.map((data: any) => {
        let duration = 10000;
        if (data.type !== "passive") {
          duration = null;
        }
        return {
          subject: data.subject,
          content: data.content,
          duration: duration,
        };
      });
      teeSheetToast.show(toastList);
    }

    setState(prevState => ({ ...prevState, notifications: notificationRes.data }));
  }

  function calculateDateBarNumberOfDaysDesktop() {
    if (windowSize.width > 1400) {
      return 7;
    } else if (windowSize.width > 1200) {
      return 5;
    } else if (windowSize.width > TABLET_WIDTH) {
      return 3;
    } else {
      return 0;
    }
  }

  function calculateDateBarNumberOfDaysTablet() {
    if (windowSize.width <= TABLET_WIDTH) {
      return 7;
    } else {
      return 0;
    }
  }

  function changeTeeSheetDate(selectedDate: Date) {
    setTeetimeTabs(undefined);
    dispatch(selectDate(selectedDate));
  }

  function updateTeeTimes(teeTimes: Array<ITeeTimeNew>) {
    setTeetimeTabs(teeTimes);
  }

  function reloadTeeTimesAndSheet() {
    if (
      state.currentCourseId &&
      state.currentDivisionId &&
      teeSheetStore.selectedDate &&
      facilityStore.teeSheetSettings?.validate_power_cart_count != null
    ) {
      void loadTeeTimeTabs(state.currentDivisionId, state.currentDivisions);
      void loadTeeSheet();
    }
  }

  return (
    <>
      <NewStatisticsBar teeSheet={teeSheet} teetimes={teetimeTabs} reloadTeeTimesAndSheet={reloadTeeTimesAndSheet} />

      <div className="content-container">
        {/* DESKTOP DATE SELECTOR */}
        <div className="flex flex-wrap-reverse flex-row gap-y-3 justify-between items-center mt-4 mb-4">
          <div className="teesheet-date-selection-header">
            <div className="teesheet-date-selection-header-input">
              <DatePickerInput
                months={1}
                position={windowSize.width <= MOBILE_WIDTH ? "center" : "left"}
                centeredInputText={windowSize.width <= MOBILE_WIDTH}
                startingDate={teeSheetStore.selectedDate as Date}
                setStartingDate={changeTeeSheetDate}
              />
            </div>
            {calculateDateBarNumberOfDaysDesktop() > 0 && (
              <DateBar
                numberOfDays={calculateDateBarNumberOfDaysDesktop()}
                onClick={changeTeeSheetDate}
                selectedDate={teeSheetStore.selectedDate as Date}
              />
            )}
          </div>
          <div className="teesheet-filters">
            <div className="teesheet-filter-search">
              <Input
                labelHidden
                onChange={e => setState(prevState => ({ ...prevState, teeTimeSearch: e.target.value }))}
                placeholder={t("elements.tee_sheet.new_tee_sheet.002")}
              />
            </div>
            {state.currentCourseId && state.currentDivisionId && state.currentDivisions && (
              <>
                {facilityStore?.facility?.courses?.length > 1 && (
                  <div className="teesheet-filter">
                    <Select
                      placeholder={t("elements.tee_sheet.new_tee_sheet.003")}
                      onChange={val => handleCourseChange(val)}
                      defaultValue={state.currentCourseId}
                    >
                      {facilityStore.facility.courses.map((course: ICourse, i: number) => (
                        <Select.Option key={i} value={course.id} name={course.long_name}>
                          <span>{course.long_name}</span>
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                )}
                <div className="teesheet-filter">
                  <Select
                    placeholder={t("elements.tee_sheet.new_tee_sheet.004")}
                    onChange={val => setState(prevState => ({ ...prevState, currentDivisionId: val }))}
                    defaultValue={state.currentDivisionId}
                  >
                    {state.currentDivisions.map((division: IDivision, i: number) => (
                      <Select.Option key={i} value={division.id} name={division.title}>
                        <span>{division.title}</span>
                      </Select.Option>
                    ))}
                    <Select.Option value={-1} name="Combined">
                      <span>{t("elements.tee_sheet.new_tee_sheet.005")}</span>
                    </Select.Option>
                  </Select>
                </div>
              </>
            )}
          </div>
        </div>

        {/* TABLET DATE SELECTOR */}
        {calculateDateBarNumberOfDaysTablet() > 0 && (
          <div className="relative">
            <div
              className="absolute flex justify-center items-center cursor-pointer text-left"
              style={{
                width: "15px",
                top: "20px",
                left: "-8px",
                display: windowSize.width <= MOBILE_WIDTH ? "block" : "none",
              }}
              onClick={() => (tabletDateBar.current.scrollLeft -= 75)}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </div>
            <div className="overflow-x-scroll pb-2 mb-2" ref={tabletDateBar}>
              <DateBar
                numberOfDays={calculateDateBarNumberOfDaysTablet()}
                onClick={changeTeeSheetDate}
                selectedDate={teeSheetStore.selectedDate as Date}
              />
            </div>
            <div
              className="absolute flex justify-center items-center cursor-pointer text-right"
              style={{
                width: "15px",
                top: "20px",
                right: "-8px",
                display: windowSize.width <= MOBILE_WIDTH ? "block" : "none",
              }}
              onClick={() => (tabletDateBar.current.scrollLeft += 75)}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </div>
          </div>
        )}

        {!teetimeTabs ? (
          <div style={{ height: "24px", overflow: "hidden" }}>
            <Spin />
          </div>
        ) : (
          <>
            {teetimeTabs.length > 0 ? (
              <NewTeeSheetTabs
                teetimeTabs={teetimeTabs}
                teetimeBookingCategories={teeTimeBookingCategories}
                divisions={state.currentDivisions}
                reloadTeeTimeTabs={() => {
                  if (
                    state.currentCourseId &&
                    state.currentDivisionId &&
                    teeSheetStore.selectedDate &&
                    facilityStore.teeSheetSettings?.validate_power_cart_count != null
                  ) {
                    void loadTeeTimeTabs(state.currentDivisionId, state.currentDivisions);
                  }
                }}
                reloadActionTeeTimeTabs={loadActionTeeTimeTabs}
                setTeeTimes={teeTimes => updateTeeTimes(teeTimes)}
                teeTimeSearch={state.teeTimeSearch}
                loadingTeetimeTabActions={loadingTeetimeTabActions}
                setLoadingTeetimeTabActions={setLoadingTeetimeTabActions}
              />
            ) : (
              <Callout
                type="error"
                title={t("elements.tee_sheet.new_tee_sheet.006")}
                content={t("elements.tee_sheet.new_tee_sheet.007")}
                actions={{
                  primary: {
                    label: t("elements.tee_sheet.new_tee_sheet.008"),
                    onClick: () => history.push(`/admin/settings/tee-sheet/calendar`),
                  },
                }}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
